/**
 * Theme System for FL/TX Surety Bond Intelligence Platform
 * Provides distinct branding and colors for Florida (FDOT) and Texas (TxDOT)
 */

export const FloridaTheme = {
  name: 'Florida',
  abbr: 'FDOT',
  state: 'FL',
  fullName: 'Florida Department of Transportation',

  // Brand Identity
  tagline: 'Ocean State Opportunities',

  // Color Palette
  colors: {
    primary: '#0066CC',        // Ocean Blue
    primaryDark: '#004C99',    // Deep Ocean
    primaryLight: '#4A90E2',   // Sky Blue
    secondary: '#2DD4BF',      // Seafoam Green
    accent: '#FF6B6B',         // Coral (hot prospects)
    success: '#10B981',        // Green
    warning: '#F59E0B',        // Amber
    error: '#EF4444',          // Red
    info: '#3B82F6',           // Blue

    // Backgrounds
    background: '#F5F5DC',     // Sand
    backgroundAlt: '#FFFFFF',  // White
    surface: '#FFFFFF',        // White

    // Text
    text: '#1A2B4A',           // Deep Navy
    textSecondary: '#64748B',  // Slate
    textLight: '#94A3B8',      // Light Slate

    // Gradients
    gradientStart: '#0066CC',
    gradientEnd: '#2DD4BF'
  },

  // Typography
  fonts: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    accent: "'Poppins', sans-serif",
    mono: "'JetBrains Mono', monospace"
  },

  // Component Styles
  components: {
    card: {
      borderRadius: '12px',
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      shadowHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    },
    button: {
      borderRadius: '8px',
      paddingX: '16px',
      paddingY: '8px'
    },
    badge: {
      borderRadius: '6px',
      paddingX: '8px',
      paddingY: '4px'
    }
  }
};

export const TexasTheme = {
  name: 'Texas',
  abbr: 'TxDOT',
  state: 'TX',
  fullName: 'Texas Department of Transportation',

  // Brand Identity
  tagline: 'Lone Star Opportunities',

  // Color Palette
  colors: {
    primary: '#BF5700',        // Texas Orange
    primaryDark: '#8B3A00',    // Deep Burnt Orange
    primaryLight: '#CC5500',   // Bright Orange
    secondary: '#C8A882',      // Longhorn Tan
    accent: '#8B0000',         // Deep Red (hot prospects)
    success: '#10B981',        // Green
    warning: '#F59E0B',        // Amber
    error: '#EF4444',          // Red
    info: '#3B82F6',           // Blue

    // Backgrounds
    background: '#F8F4E6',     // Prairie Cream
    backgroundAlt: '#FFFFFF',  // White
    surface: '#FFFFFF',        // White

    // Text
    text: '#2C2C2C',           // Charcoal
    textSecondary: '#64748B',  // Slate
    textLight: '#94A3B8',      // Light Slate

    // Gradients
    gradientStart: '#BF5700',
    gradientEnd: '#C8A882'
  },

  // Typography
  fonts: {
    primary: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    accent: "'Roboto Slab', serif",
    mono: "'JetBrains Mono', monospace"
  },

  // Component Styles
  components: {
    card: {
      borderRadius: '8px',
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      shadowHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    },
    button: {
      borderRadius: '6px',
      paddingX: '16px',
      paddingY: '8px'
    },
    badge: {
      borderRadius: '4px',
      paddingX: '8px',
      paddingY: '4px'
    }
  }
};

export const NorthCarolinaTheme = {
  name: 'North Carolina',
  abbr: 'NCDOT',
  state: 'NC',
  fullName: 'North Carolina Department of Transportation',
  tagline: 'Tar Heel Opportunities',
  colors: {
    primary: '#004B87',
    primaryDark: '#003366',
    primaryLight: '#2E5C8A',
    secondary: '#D3A625',
    accent: '#E74C3C',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
    background: '#F5F7FA',
    backgroundAlt: '#FFFFFF',
    surface: '#FFFFFF',
    text: '#1A2B4A',
    textSecondary: '#64748B',
    textLight: '#94A3B8',
    gradientStart: '#004B87',
    gradientEnd: '#D3A625'
  },
  fonts: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    accent: "'Poppins', sans-serif",
    mono: "'JetBrains Mono', monospace"
  },
  components: {
    card: { borderRadius: '10px', shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', shadowHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' },
    button: { borderRadius: '7px', paddingX: '16px', paddingY: '8px' },
    badge: { borderRadius: '5px', paddingX: '8px', paddingY: '4px' }
  }
};

/**
 * Get theme based on state
 * @param {string} state - 'FL', 'TX', or 'NC'
 * @returns {Object} Theme object
 */
export const GeorgiaTheme = {
  name: 'Georgia', abbr: 'GDOT', state: 'GA',
  fullName: 'Georgia Department of Transportation',
  tagline: 'Peach State Opportunities',
  colors: {
    primary: '#B3282D', primaryDark: '#8B1A1E', primaryLight: '#D44A4F',
    secondary: '#D4A843', accent: '#E74C3C', success: '#10B981',
    warning: '#F59E0B', error: '#EF4444', info: '#3B82F6',
    background: '#FBF8F5', backgroundAlt: '#FFFFFF', surface: '#FFFFFF',
    text: '#1A2B4A', textSecondary: '#64748B', textLight: '#94A3B8',
    gradientStart: '#B3282D', gradientEnd: '#D4A843'
  },
  fonts: { primary: "'Inter', -apple-system, sans-serif", accent: "'Poppins', sans-serif", mono: "'JetBrains Mono', monospace" },
  components: {
    card: { borderRadius: '10px', shadow: '0 4px 6px -1px rgba(0,0,0,0.1)', shadowHover: '0 10px 15px -3px rgba(0,0,0,0.1)' },
    button: { borderRadius: '7px', paddingX: '16px', paddingY: '8px' },
    badge: { borderRadius: '5px', paddingX: '8px', paddingY: '4px' }
  }
};

export const VirginiaTheme = {
  name: 'Virginia', abbr: 'VDOT', state: 'VA',
  fullName: 'Virginia Department of Transportation',
  tagline: 'Old Dominion Opportunities',
  colors: {
    primary: '#1B3A5C', primaryDark: '#0F2640', primaryLight: '#2E5C8A',
    secondary: '#C4A35A', accent: '#E74C3C', success: '#10B981',
    warning: '#F59E0B', error: '#EF4444', info: '#3B82F6',
    background: '#F7F9FB', backgroundAlt: '#FFFFFF', surface: '#FFFFFF',
    text: '#1A2B4A', textSecondary: '#64748B', textLight: '#94A3B8',
    gradientStart: '#1B3A5C', gradientEnd: '#C4A35A'
  },
  fonts: { primary: "'Inter', -apple-system, sans-serif", accent: "'Poppins', sans-serif", mono: "'JetBrains Mono', monospace" },
  components: {
    card: { borderRadius: '10px', shadow: '0 4px 6px -1px rgba(0,0,0,0.1)', shadowHover: '0 10px 15px -3px rgba(0,0,0,0.1)' },
    button: { borderRadius: '7px', paddingX: '16px', paddingY: '8px' },
    badge: { borderRadius: '5px', paddingX: '8px', paddingY: '4px' }
  }
};

export function getTheme(state) {
  if (state === 'TX') return TexasTheme;
  if (state === 'NC') return NorthCarolinaTheme;
  if (state === 'GA') return GeorgiaTheme;
  if (state === 'VA') return VirginiaTheme;
  return FloridaTheme;
}

/* ──────────────────────────────────────────────────────────────────────
 * App theme (look & feel) — independent of the state brand palettes above.
 *
 *   stark  — default. Near-black navy HUD, arc-reactor cyan + Stark gold.
 *   dark   — "Classic Dark", the original dark mode.
 *   light  — "Classic Light", the original light mode.
 *
 * The value is written to `data-theme` on <html>; index.css holds one token
 * block per theme. public/index.html runs a copy of getInitialTheme() inline
 * before first paint — keep the storage key and the default in sync with it.
 * ────────────────────────────────────────────────────────────────────── */

export const THEMES = [
  { id: 'stark', label: 'Stark' },
  { id: 'dark', label: 'Classic Dark' },
  { id: 'light', label: 'Classic Light' },
];
export const DEFAULT_THEME = 'stark';
export const THEME_STORAGE_KEY = 'bondhq-theme';

const THEME_IDS = THEMES.map((t) => t.id);
const isTheme = (v) => THEME_IDS.includes(v);

/** Set `data-theme` on <html>. Unknown values fall back to the default. */
export function applyTheme(theme) {
  const next = isTheme(theme) ? theme : DEFAULT_THEME;
  document.documentElement.setAttribute('data-theme', next);
  return next;
}

/** The theme currently on <html> (what the inline boot script applied). */
export function getCurrentTheme() {
  if (typeof document === 'undefined') return DEFAULT_THEME;
  const v = document.documentElement.getAttribute('data-theme');
  return isTheme(v) ? v : DEFAULT_THEME;
}

/** Saved theme, else the Stark default. */
export function getInitialTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(saved)) return saved;
  } catch { /* localStorage unavailable */ }
  return DEFAULT_THEME;
}

export function saveTheme(theme) {
  try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* noop */ }
}

/** @deprecated use applyTheme — kept so older imports keep working. */
export const applyColorMode = applyTheme;
/** @deprecated use getInitialTheme. */
export const getInitialColorMode = getInitialTheme;

export default { FloridaTheme, TexasTheme, NorthCarolinaTheme, getTheme };
